import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { canDownloadClientArtifacts } from '@shared/auth/cognito';
import { statusPillClassName } from '@shared/utils/statusPill';
import {
  ApiRecord,
  canClientDownloadPackage,
  packageCounts,
  packageScopeExceptionSummary,
  packageScopeReviewMessages,
  hasPackageScopeReviewContext,
  packageScopeSummary,
  packageId,
  packageName,
  packageStage,
  packageStageLabel,
  packageStatus,
  packageType,
  packageUpdatedAtDisplay,
  recordsFromResponse,
  text,
  valueOrDash,
} from './packageUi';
import { openPackageDownload, type PackageDownloadStatus } from './packageDownload';
import { useClientAiAssistant } from '../components/ClientAiAssistantDrawer';
import { useClientAccount } from '../account/AccountContext';
import PackageGenerationBanner from '../components/PackageGenerationBanner';


type PackageFilter = 'ALL' | 'TECHNICAL_EVIDENCE_PACKAGE' | 'FINAL_AUDITOR_PACKAGE' | 'CLIENT_ACTION_REQUIRED' | 'AUDITOR_PACKAGE_READY';
type SearchMode = 'contains' | 'beginsWith' | 'exact';

type PackagePageMetadata = {
  count?: number;
  matchedCount?: number;
  page?: number;
  pageCount?: number;
  limit?: number;
};

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const PACKAGE_FILTERS: Array<{ value: PackageFilter; label: string }> = [
  { value: 'ALL', label: 'All packages' },
  { value: 'TECHNICAL_EVIDENCE_PACKAGE', label: 'Technical evidence' },
  { value: 'FINAL_AUDITOR_PACKAGE', label: 'Final auditor package' },
  { value: 'CLIENT_ACTION_REQUIRED', label: 'Client action required' },
  { value: 'AUDITOR_PACKAGE_READY', label: 'Final auditor package ready' },
];

const SEARCH_MODES: Array<{ value: SearchMode; label: string }> = [
  { value: 'contains', label: 'Contains' },
  { value: 'beginsWith', label: 'Begins with' },
  { value: 'exact', label: 'Exact term' },
];

function packageMatchesFilter(pkg: ApiRecord, filter: PackageFilter): boolean {
  if (filter === 'ALL') return true;
  const haystack = JSON.stringify(pkg).toUpperCase();
  if (filter === 'TECHNICAL_EVIDENCE_PACKAGE' || filter === 'FINAL_AUDITOR_PACKAGE') {
    return packageType(pkg).toUpperCase() === filter;
  }
  if (filter === 'CLIENT_ACTION_REQUIRED') {
    return haystack.includes('CLIENT_ACTION_REQUIRED') || haystack.includes('PENDING') || haystack.includes('PARTIAL');
  }
  if (filter === 'AUDITOR_PACKAGE_READY') {
    return haystack.includes('AUDITOR_PACKAGE_READY') || haystack.includes('FINAL_ASSESSOR_REVIEW_PENDING') || haystack.includes('FINAL_AUDITOR_READY');
  }
  return true;
}

function readinessBadges(pkg: ApiRecord) {
  const counts = packageCounts(pkg);
  if (!counts.total && !counts.pass && !counts.partial && !counts.fail && !counts.notApplicable) return '—';
  return (
    <div className="readiness-badge-row table-readiness-badges">
      <span className="readiness-badge readiness-badge-success">PASS <strong>{counts.pass}</strong></span>
      <span className="readiness-badge readiness-badge-warning">PARTIAL <strong>{counts.partial}</strong></span>
      <span className="readiness-badge readiness-badge-error">FAIL <strong>{counts.fail}</strong></span>
      <span className="readiness-badge readiness-badge-muted">N/A <strong>{counts.notApplicable}</strong></span>
      <span className="readiness-badge readiness-badge-blue">TOTAL <strong>{counts.total}</strong></span>
    </div>
  );
}

function isFinalAuditorPackage(pkg: ApiRecord): boolean {
  return canClientDownloadPackage(pkg);
}

function isTechnicalEvidencePackage(pkg: ApiRecord): boolean {
  return packageType(pkg).toUpperCase() === 'TECHNICAL_EVIDENCE_PACKAGE'
    || packageStage(pkg).toUpperCase() === 'TECHNICAL_BASELINE';
}

function relatedTechnicalPackageId(pkg: ApiRecord): string {
  return text(
    pkg,
    'sourceTechnicalPackageId',
    'technicalPackageId',
    'sourcePackageId',
    'sourceEvidencePackageId',
    'evidencePackageId',
    'relatedTechnicalPackageId',
  );
}

const FINAL_PACKAGE_GENERATION_PENDING_STATUSES = new Set([
  'COMPLETED_EVIDENCE_PACKAGE_BUILD_REQUESTED',
  'COMPLETED_EVIDENCE_PACKAGE_BUILD_IN_PROGRESS',
  'COMPLETED_EVIDENCE_PACKAGE_UPLOADED_TO_INTAKE',
  'SUBMITTED_FOR_FINAL_AUDITOR_PACKAGE',
  'FINAL_AUDITOR_PACKAGE_REQUESTED',
  'FINAL_AUDITOR_PACKAGE_GENERATION_REQUESTED',
  'FINAL_AUDITOR_PACKAGE_GENERATION_IN_PROGRESS',
  'FINAL_ASSESSOR_REVIEW_PENDING',
]);

function packageStatusValues(pkg: ApiRecord): string[] {
  return ['workspaceStatus', 'packageStatus', 'status', 'packageLifecycleStatus', 'jobStatus', 'displayStatus']
    .map((key) => text(pkg, key).trim().toUpperCase())
    .filter(Boolean);
}

function finalAuditorPackageGenerationPending(pkg: ApiRecord, allPackages: ApiRecord[]): boolean {
  const id = packageId(pkg);
  if (!id || !isTechnicalEvidencePackage(pkg) || relatedFinalPackage(pkg, allPackages)) return false;
  return packageStatusValues(pkg).some((status) => FINAL_PACKAGE_GENERATION_PENDING_STATUSES.has(status));
}

function packageRelation(pkg: ApiRecord, allPackages: ApiRecord[]) {
  const id = packageId(pkg);
  if (isFinalAuditorPackage(pkg)) {
    const sourceId = relatedTechnicalPackageId(pkg);
    return <span className="cell-secondary">{sourceId ? `Generated from: ${sourceId}` : 'Generated final auditor package'}</span>;
  }

  if (isTechnicalEvidencePackage(pkg) && id) {
    const finalPackageName = text(pkg, 'relatedFinalPackageName');
    const finalPackageId = text(pkg, 'relatedFinalPackageId');
    if (finalPackageName || finalPackageId) {
      return <span className="cell-secondary">Generated final auditor package: {finalPackageName || finalPackageId}</span>;
    }

    const finalPackage = allPackages.find((candidate) => isFinalAuditorPackage(candidate) && relatedTechnicalPackageId(candidate) === id);
    if (finalPackage) {
      return <span className="cell-secondary">Generated final auditor package: {packageName(finalPackage)}</span>;
    }
    if (finalAuditorPackageGenerationPending(pkg, allPackages)) {
      return (
        <span className="related-package-pending">
          Final auditor package is being generated…
        </span>
      );
    }
    return <span className="cell-secondary">Final package: not generated yet</span>;
  }

  return <span className="cell-secondary">—</span>;
}

function relatedFinalPackage(pkg: ApiRecord, allPackages: ApiRecord[]): ApiRecord | undefined {
  const id = packageId(pkg);
  if (!id || !isTechnicalEvidencePackage(pkg)) return undefined;
  if (text(pkg, 'relatedFinalPackageId', 'relatedFinalPackageName')) return pkg;
  return allPackages.find((candidate) => isFinalAuditorPackage(candidate) && relatedTechnicalPackageId(candidate) === id);
}

function workspaceClosed(pkg: ApiRecord, allPackages: ApiRecord[]): boolean {
  if (Boolean(pkg.workspaceLocked)) return true;
  return Boolean(relatedFinalPackage(pkg, allPackages));
}

function shouldShowPackageIdLine(pkg: ApiRecord): boolean {
  const id = packageId(pkg);
  const name = packageName(pkg);
  return Boolean(id && id !== name);
}

function responseMetadata(response: unknown): PackagePageMetadata {
  const root = response && typeof response === 'object' && !Array.isArray(response) ? response as PackagePageMetadata : {};
  return {
    count: root.count,
    matchedCount: root.matchedCount,
    page: root.page,
    pageCount: root.pageCount,
    limit: root.limit,
  };
}

function buildPackageQuery(page: number, limit: number, search: string, searchMode: SearchMode, filter: PackageFilter): string {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('page', String(page));
  if (search.trim()) {
    params.set('search', search.trim());
    params.set('searchMode', searchMode);
  }
  if (filter === 'TECHNICAL_EVIDENCE_PACKAGE' || filter === 'FINAL_AUDITOR_PACKAGE') {
    params.set('packageType', filter);
  }
  return params.toString();
}

export default function EvidencePackagesPage() {
  const { accountPath } = useClientAccount();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [packages, setPackages] = useState<ApiRecord[]>([]);
  const [filter, setFilter] = useState<PackageFilter>('ALL');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('contains');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [metadata, setMetadata] = useState<PackagePageMetadata>({ page: 1, pageCount: 0, limit: DEFAULT_PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<PackageDownloadStatus>({ state: 'idle' });
  const canDownloadArtifacts = canDownloadClientArtifacts();
  const { setPageScope } = useClientAiAssistant();
  const packageRegistryHelp = canDownloadArtifacts
    ? 'Client admins can download final auditor evidence packages. Technical evidence packages open the workspace for review.'
    : 'You have read-only access. You can review package status and details, but downloads are available only to client administrators.';

  async function loadPackages(nextPage = page, nextSearch = appliedSearch) {
    setLoading(true);
    setError(null);
    try {
      const query = buildPackageQuery(nextPage, limit, nextSearch, searchMode, filter);
      const response = await api.get<unknown>(`/client/packages?${query}`);
      const records = recordsFromResponse(response, ['packages', 'items']);
      const visibleRecords = (filter === 'CLIENT_ACTION_REQUIRED' || filter === 'AUDITOR_PACKAGE_READY')
        ? records.filter((pkg) => packageMatchesFilter(pkg, filter))
        : records;
      setPackages(visibleRecords);
      setMetadata(responseMetadata(response));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load evidence packages.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPackages(page, appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, page, appliedSearch, searchMode, filter, limit]);

  const hasPendingFinalAuditorPackage = packages.some((pkg) =>
    finalAuditorPackageGenerationPending(pkg, packages),
  );

  useEffect(() => {
    if (!hasPendingFinalAuditorPackage || loading) return undefined;

    const refreshTimer = window.setTimeout(() => {
      void loadPackages(page, appliedSearch);
    }, 10_000);

    return () => window.clearTimeout(refreshTimer);
    // loadPackages intentionally uses the current page/filter state captured by this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPendingFinalAuditorPackage, loading, page, appliedSearch, searchMode, filter, limit]);

  function applySearch() {
    setPage(1);
    setAppliedSearch(search.trim());
  }

  function clearSearch() {
    setSearch('');
    setAppliedSearch('');
    setPage(1);
  }

  async function handleDownload(pkg: ApiRecord) {
    const id = packageId(pkg);
    if (!canDownloadArtifacts) {
      setDownloadStatus({
        packageId: id,
        state: 'error',
        message: 'Downloads are available only to client administrators. Your current role can still review package status and details.',
      });
      return;
    }
    setDownloadStatus({ packageId: id, state: 'preparing' });
    try {
      await openPackageDownload(api, pkg);
      setDownloadStatus({ state: 'idle' });
    } catch (err) {
      setDownloadStatus({
        packageId: id,
        state: 'error',
        message: err instanceof Error ? err.message : 'Unable to prepare package download.',
      });
    }
  }

  function setAiScopeForPackageDetail(pkg: ApiRecord) {
    const id = packageId(pkg);
    if (!id) return;
    const name = packageName(pkg) || id;
    const type = packageType(pkg) || undefined;
    setPageScope({
      packageId: id,
      selectedPackageId: id,
      packageName: name,
      packageType: type,
      pageType: 'PACKAGE_DETAIL',
      sourceLabel: 'Client package detail page',
      pageContext: {
        pageType: 'PACKAGE_DETAIL',
        packageId: id,
        packageName: name,
        packageType: type,
        sourceLabel: 'Client package detail page',
      },
    });
  }

  const pageCount = metadata.pageCount ?? 0;
  const matchedCount = metadata.matchedCount ?? packages.length;
  const currentPage = metadata.page ?? page;

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Evidence Packages</h2>
        <p>Review indexed technical evidence packages and final auditor package status.</p>
      </div>

      <PackageGenerationBanner />

      <article className="card">
        <div className="section-title-row">
          <div>
            <h3>Package registry</h3>
            <p className="muted-text">
              {packageRegistryHelp}
            </p>
          </div>
          <button className="secondary-button" type="button" onClick={() => void loadPackages(page, appliedSearch)}>Refresh</button>
        </div>

        <div className="filter-grid package-filter-grid">
          <label>
            Search
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') applySearch();
              }}
              placeholder="Package, status, stage, job, related package"
            />
          </label>
          <label>
            Search mode
            <select value={searchMode} onChange={(event) => { setSearchMode(event.target.value as SearchMode); setPage(1); }}>
              {SEARCH_MODES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label>
            Package filter
            <select value={filter} onChange={(event) => { setFilter(event.target.value as PackageFilter); setPage(1); }}>
              {PACKAGE_FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
          <label>
            Limit
            <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}>
              {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <div className="filter-actions">
            <button className="primary-button" type="button" onClick={applySearch}>Search</button>
            <button className="secondary-button" type="button" onClick={clearSearch} disabled={!search && !appliedSearch}>Clear</button>
          </div>
        </div>

        <div className="filter-result-row">
          <span className="muted-text">Showing {packages.length} of {matchedCount} matching packages. Limit: {metadata.limit ?? limit}.</span>
        </div>

        {loading && <p>Loading package registry...</p>}
        {error && <p className="error-text">{error}</p>}
        {!loading && !error && packages.length === 0 && <p>No indexed evidence packages match the current search or filter.</p>}

        {downloadStatus.state === 'error' && downloadStatus.message && (
          <p className="error-text">{downloadStatus.message}</p>
        )}

        {!loading && !error && packages.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="data-table interactive-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th>Readiness</th>
                    <th>Scope</th>
                    <th>Related package</th>
                    <th>Updated</th>
                    <th>Workspace</th>
                    <th>Details</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg, index) => {
                    const id = packageId(pkg);
                    const finalPackage = isFinalAuditorPackage(pkg);
                    const technicalPackage = isTechnicalEvidencePackage(pkg);
                    const workspacePackageId = technicalPackage ? id : (finalPackage ? relatedTechnicalPackageId(pkg) : '');
                    const closedWorkspace = technicalPackage ? workspaceClosed(pkg, packages) : Boolean(finalPackage && workspacePackageId);
                    const isPreparingDownload = downloadStatus.state === 'preparing' && downloadStatus.packageId === id;
                    return (
                      <tr key={id || index} className={closedWorkspace ? 'package-row-closed' : undefined}>
                        <td>
                          {id ? <Link className="table-link" to={accountPath(`/evidence-packages/${encodeURIComponent(id)}`)} onClick={() => setAiScopeForPackageDetail(pkg)}>{packageName(pkg)}</Link> : packageName(pkg)}
                          {shouldShowPackageIdLine(pkg) && <div className="cell-secondary">{id}</div>}
                        </td>
                        <td><span className={statusPillClassName(packageStage(pkg) || packageStageLabel(pkg))}>{valueOrDash(packageStageLabel(pkg))}</span></td>
                        <td><span className={statusPillClassName(packageStatus(pkg))}>{packageStatus(pkg)}</span></td>
                        <td>{readinessBadges(pkg)}</td>
                        <td>
                          <div>{packageScopeSummary(pkg)}</div>
                          <div className="cell-secondary">{packageScopeExceptionSummary(pkg)}</div>
                          {hasPackageScopeReviewContext(pkg) && <div className="cell-secondary">{packageScopeReviewMessages(pkg).join(' ')}</div>}
                        </td>
                        <td>{packageRelation(pkg, packages)}</td>
                        <td>{valueOrDash(packageUpdatedAtDisplay(pkg))}</td>
                        <td>
                          {workspacePackageId ? (
                            <Link
                              className={`secondary-button compact-action-button ${closedWorkspace ? 'readonly-workspace-button' : ''}`}
                              to={accountPath(`/evidence-workspace/${encodeURIComponent(workspacePackageId)}`)}
                              title={closedWorkspace ? 'Open the related evidence workspace in read-only mode' : 'Open workspace'}
                            >
                              Open Workspace
                            </Link>
                          ) : (
                            <span className="muted-text">—</span>
                          )}
                        </td>
                        <td>
                          {id ? (
                            <Link className="secondary-button compact-action-button" to={accountPath(`/evidence-packages/${encodeURIComponent(id)}`)} onClick={() => setAiScopeForPackageDetail(pkg)}>
                              View Details
                            </Link>
                          ) : (
                            <span className="muted-text">—</span>
                          )}
                        </td>
                        <td>
                          {id && finalPackage && canDownloadArtifacts ? (
                            <button className="primary-button compact-action-button" type="button" onClick={() => void handleDownload(pkg)} disabled={isPreparingDownload}>
                              {isPreparingDownload ? 'Preparing...' : 'Download'}
                            </button>
                          ) : id && finalPackage ? (
                            <span className="status-pill status-pill-muted" title="Downloads are available only to client administrators.">Admin only</span>
                          ) : technicalPackage ? (
                            <span className="muted-text">Workspace only</span>
                          ) : (
                            <span className="muted-text">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination-bar">
              <button className="secondary-button" type="button" onClick={() => setPage(1)} disabled={currentPage <= 1}>First</button>
              <button className="secondary-button" type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={currentPage <= 1}>Previous</button>
              <span className="muted-text">Page {currentPage} of {pageCount || 1}</span>
              <button className="secondary-button" type="button" onClick={() => setPage((value) => value + 1)} disabled={!pageCount || currentPage >= pageCount}>Next</button>
              <button className="secondary-button" type="button" onClick={() => setPage(pageCount || 1)} disabled={!pageCount || currentPage >= pageCount}>Last</button>
            </div>
          </>
        )}
      </article>
    </div>
  );
}
